import OmniTable from "../../omni-common/components/OmniTable";
import OmniCheckbox from "../../omni-common/components/OmniCheckbox";
import { formatDate } from "../../../utilities/format";
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

export interface Draft {
  approvedAt?: string;
  approvedBy?: string;
  artifactId: string;
  channelPostPlans: Array<{
    channelRef: {
      name: string;
    };
    status?: string;
  }>;
  createdAt: string;
  draftId: string;
  originalPostId: string;
  status: "APPROVED" | "DELETED" | "EDITING" | "NEW";
  title: string;
  updatedAt: string;
}

interface DraftTableProps {
  posts: Draft[];
  selectedIds: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectPost: (draftId: string, selected: boolean) => void;
  onPostClick?: (post: Draft) => void;
  isLoading?: boolean;
  sortKey?: string;
  sortDirection?: "ASC" | "DESC";
  onSort?: (key: string, direction: "ASC" | "DESC") => void;
}

const STATUS_LABELS = {
  APPROVED: "승인",
  DELETED: "(삭제)",
  EDITING: "검토중", 
  NEW: "초안",
};

const STATUS_COLORS = {
  APPROVED: "bg-green-100 text-green-800 border border-green-200",
  DELETED: "bg-red-100 text-red-800 border border-red-200",
  EDITING: "bg-blue-100 text-blue-800 border border-blue-200",
  NEW: "bg-gray-100 text-gray-800 border border-gray-200",
};


export default function DraftTable({
  posts,
  selectedIds,
  onSelectAll,
  onSelectPost,
  onPostClick,
  isLoading,
  sortKey,
  sortDirection,
  onSort,
}: DraftTableProps) {
  // 생성된 채널이 없는 원고들만 전체 선택 대상으로 계산
  const selectablePosts = posts.filter(post => {
    const hasCreatedChannel = post.channelPostPlans?.some(plan =>
      plan.status === 'POST_CREATED'
    );
    return !hasCreatedChannel;
  });
  const isAllSelected = selectablePosts.length > 0 && selectedIds.length === selectablePosts.length;

  const handleSort = (key: string) => {
    if (!onSort) return;

    // 첫 클릭: ASC, 두 번째 클릭: DESC, 세 번째 클릭: 정렬 초기화
    if (sortKey === key) {
      if (sortDirection === "ASC") {
        onSort(key, "DESC");
      } else {
        // 정렬 초기화 (빈 문자열로 전달)
        onSort("", "ASC");
      }
    } else {
      onSort(key, "ASC");
    }
  };

  const SortableHeader = ({ 
    sortKey: key, 
    children, 
    className,
    style 
  }: { 
    sortKey?: string; 
    children: React.ReactNode; 
    className?: string;
    style?: React.CSSProperties;
  }) => {
    const isSortable = key && onSort;
    const isActive = sortKey === key;
    
    if (!isSortable) {
      return <div className={className} style={style}>{children}</div>;
    }

    const getSortIcon = () => {
      if (isActive) {
        return sortDirection === "ASC" ? (
          <ChevronUpIcon className="h-4 w-4 ml-1" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        );
      }
      return <ChevronUpDownIcon className="h-4 w-4 ml-1" />;
    };

    return (
      <div 
        className={`${className} cursor-pointer transition-colors hover:bg-gray-800 ${
          isActive ? 'bg-gray-800' : ''
        }`}
        style={style}
        onClick={() => handleSort(key)}
      >
        {children}
        {getSortIcon()}
      </div>
    );
  };



  return (
    <div className="relative h-full flex flex-col">
      <OmniTable.Wrapper className="flex-1">
      <div className="flex w-full items-center rounded-t-lg border border-black bg-black py-2">
        <div className="flex items-center justify-center px-2 text-sm font-bold text-white" style={{ width: '63px', minWidth: '63px' }}>
          <OmniCheckbox
            isChecked={isAllSelected}
            onCheck={onSelectAll}
            disabled={selectablePosts.length === 0}
          />
        </div>
        <div
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '120px', minWidth: '120px' }}
        >
          상태
        </div>
        <SortableHeader
          sortKey="title"
          className="flex items-center justify-center px-2 text-sm font-bold text-white flex-1"
          style={{ minWidth: '567px' }}
        >
          제목
        </SortableHeader>
        <div 
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '200px', minWidth: '200px' }}
        >
          생성된 채널
        </div>
        <SortableHeader 
          sortKey="updatedAt"
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '105px', minWidth: '105px' }}
        >
          수정일자
        </SortableHeader>
        <SortableHeader 
          sortKey="createdAt"
          className="flex items-center justify-center px-2 text-sm font-bold text-white"
          style={{ width: '105px', minWidth: '105px' }}
        >
          생성일자
        </SortableHeader>
      </div>
      
      <OmniTable.Body isLoading={isLoading} isEmpty={!posts?.length}>
        {posts?.map((post) => {
          const isSelected = selectedIds.includes(post.draftId);
          const hasCreatedChannel = post.channelPostPlans?.some(plan =>
            plan.status === 'POST_CREATED'
          );

          return (
            <div
              key={post.draftId}
              className="relative"
              data-post-id={post.draftId}
            >
              <OmniTable.Row
                onClick={() => onPostClick?.(post)}
              >
              <div
                className="flex items-center justify-center border-e px-2"
                style={{ width: '63px', minWidth: '63px' }}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <OmniCheckbox
                    isChecked={isSelected}
                    onCheck={(checked: boolean) => onSelectPost(post.draftId, checked)}
                    disabled={hasCreatedChannel}
                  />
                </div>
              </div>
              
              <div 
                className="flex items-center justify-center border-e px-2"
                style={{ width: '120px', minWidth: '120px' }}
              >
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    STATUS_COLORS[post.status]
                  }`}
                >
                  {STATUS_LABELS[post.status]}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-start border-e px-2 flex-1"
                style={{ minWidth: '567px' }}
              >
                <span className="truncate" title={post.title}>
                  {post.title}
                </span>
              </div>
              
              <div
                className="flex items-center justify-center border-e px-2 text-sm"
                style={{ width: '200px', minWidth: '200px' }}
              >
                <div className="flex flex-wrap gap-1 justify-center">
                  {(() => {
                    const relevantPlans = post.channelPostPlans?.filter(plan =>
                      plan.status === 'POST_CREATED'
                    ) || [];

                    // 채널별로 중복 제거 (채널 이름 기준으로 unique하게)
                    const uniqueChannelMap = new Map();
                    relevantPlans.forEach(plan => {
                      const channelName = plan.channelRef.name;
                      if (!uniqueChannelMap.has(channelName)) {
                        uniqueChannelMap.set(channelName, plan);
                      }
                    });
                    const uniquePlans = Array.from(uniqueChannelMap.values());

                    return uniquePlans.length > 0 ? (
                      uniquePlans.map((plan, index) => {
                        const getStatusColor = (status: string) => {
                          switch (status) {
                            case 'POST_CREATED':
                              return 'bg-green-100 text-green-800 border border-green-200';
                            case 'POST_REQUESTED':
                              return 'bg-blue-100 text-blue-800 border border-blue-200';
                            default:
                              return 'bg-gray-100 text-gray-800 border border-gray-200';
                          }
                        };

                        return (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.status || '')}`}
                          >
                            {plan.channelRef.name}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-gray-400 text-xs">채널 없음</span>
                    );
                  })()}
                </div>
              </div>
              
              <div 
                className="flex items-center justify-center border-e px-2 text-sm"
                style={{ width: '105px', minWidth: '105px' }}
              >
                {formatDate(post.updatedAt, "YY.MM.DD")}
              </div>
              
              <div 
                className="flex items-center justify-center px-2 text-sm"
                style={{ width: '105px', minWidth: '105px' }}
              >
                {formatDate(post.createdAt, "YY.MM.DD")}
              </div>
            </OmniTable.Row>
          </div>
          );
        })}
      </OmniTable.Body>
      </OmniTable.Wrapper>
    </div>
  );
}